import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { List, Layers, Tag, Building2, AlertCircle } from "lucide-react";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import ContentBody, { buildToc } from "@/components/ContentBody";
import RelatedGrid from "@/components/RelatedGrid";
import CompuBrainSection from "@/components/CompuBrainSection";
import { getEntity, SITE_URL } from "@/lib/api";

const TYPE_META = {
  service: { label: "Service", Icon: Layers, color: "#16385f" },
  company: { label: "Platform", Icon: Building2, color: "#0b6b3a" },
  category: { label: "Category", Icon: Tag, color: "#b1560f" },
};

function Spinner() {
  return (
    <div className="flex h-[60vh] items-center justify-center" data-testid="entity-loading">
      <div className="h-9 w-9 animate-spin rounded-full border-2 border-[var(--line)] border-t-[var(--gov-blue)]" />
    </div>
  );
}

export default function EntityPage({ type: typeProp }) {
  const { slug } = useParams();
  const type = typeProp;
  const { data, isLoading, isError } = useQuery({
    queryKey: ["entity", type, slug],
    queryFn: () => getEntity(type, slug),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [type, slug]);

  if (isLoading) return <Layout><Spinner /></Layout>;

  if (isError || !data) {
    return (
      <Layout>
        <div className="mx-auto max-w-2xl px-4 py-24 text-center" data-testid="entity-error">
          <AlertCircle className="mx-auto mb-4 text-[var(--accent)]" size={40} />
          <h1 className="mb-2 text-2xl font-bold text-[var(--navy)]">Page not found</h1>
          <p className="mb-6 text-[var(--muted)]">
            We couldn't find this entry. It may have moved or the link is incorrect.
          </p>
          <Link to="/" className="font-semibold text-[var(--gov-blue)] hover:underline">
            Return to the knowledge base home
          </Link>
        </div>
      </Layout>
    );
  }

  const meta = TYPE_META[type] || TYPE_META.service;
  const path = `/${type}/${slug}`;
  const toc = buildToc(data.nodes);

  const linkRefs = [
    ...(data.rel_categories || []),
    ...(data.child_services || []),
    ...(data.rel_services || []),
    ...(data.rel_companies || []),
    ...(data.breadcrumb || []).filter((b) => b.type === "category"),
  ];

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: (data.breadcrumb || []).map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item:
          b.type === "home"
            ? SITE_URL + "/"
            : b.type === "companies"
            ? SITE_URL + "/companies"
            : b.type === "categories"
            ? SITE_URL + "/categories"
            : `${SITE_URL}/${b.type}/${b.slug}`,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: data.h1,
      description: data.meta_description,
      about: data.name,
      mainEntityOfPage: SITE_URL + path,
      publisher: {
        "@type": "Organization",
        name: "CompuBrain",
        url: "https://compubrain.com",
      },
    },
  ];
  if (data.faqs && data.faqs.length) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: data.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  const childServices = data.child_services || [];

  return (
    <Layout>
      <Seo title={data.seo_title} description={data.meta_description} path={path} type="article" jsonLd={jsonLd} />

      {/* Page header band */}
      <div className="border-b border-[var(--line)] bg-[var(--bg-alt)]">
        <div className="mx-auto max-w-[1240px] px-4 py-5 lg:px-6">
          <Breadcrumbs items={data.breadcrumb} />
          <div className="mt-3 flex items-start gap-3">
            <span
              className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-[5px] text-white"
              style={{ background: meta.color }}
            >
              <meta.Icon size={18} />
            </span>
            <div>
              <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: meta.color }}>
                {meta.label}
                {data.is_main && " · Main Category"}
                {data.group && ` · ${data.group}`}
                {data.main_category && type === "service" && ` · ${data.main_category}`}
              </span>
              <h1 className="text-3xl font-bold leading-tight text-[var(--navy)] sm:text-4xl" data-testid="entity-title">
                {data.h1}
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-[1240px] gap-8 px-4 py-8 lg:grid-cols-[240px_minmax(0,1fr)_300px] lg:px-6">
        {/* LEFT: TOC */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            {toc.length > 0 && (
              <nav className="thin-scroll max-h-[70vh] overflow-y-auto" data-testid="toc">
                <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[var(--muted)]">
                  <List size={13} /> On this page
                </div>
                <ul className="space-y-1 border-l border-[var(--line)]">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a
                        href={`#${t.id}`}
                        className="-ml-px block border-l-2 border-transparent py-1 pl-3 text-sm text-[var(--ink-soft)] hover:border-[var(--gov-blue)] hover:text-[var(--gov-blue)]"
                        data-testid={`toc-link-${t.id}`}
                      >
                        {t.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </div>
        </aside>

        {/* CENTER: content */}
        <article className="min-w-0 fade-up" data-testid="entity-article">
          {!data.has_content && (
            <div className="mb-6 rounded-[5px] border border-[var(--line)] bg-[var(--bg-panel)] px-4 py-3 text-sm text-[var(--ink-soft)]">
              This entry is part of our knowledge graph. Explore its related services, categories and
              platforms below.
            </div>
          )}

          <ContentBody nodes={data.nodes} linkRefs={linkRefs} />

          {/* Category page: services under it */}
          {childServices.length > 0 && (
            <RelatedGrid
              title={`Services in ${data.name}`}
              items={childServices}
              testid="child-services"
              cols={3}
            />
          )}

          {/* FAQs */}
          {data.faqs && data.faqs.length > 0 && (
            <section className="mt-10" data-testid="faq-section">
              <h2 className="mb-3 border-b-2 border-[var(--line)] pb-2 text-xl font-bold text-[var(--navy)]">
                Frequently Asked Questions
              </h2>
              <div className="divide-y divide-[var(--line-soft)] rounded-[5px] border border-[var(--line)] bg-white">
                {data.faqs.map((f, i) => (
                  <details key={i} className="group px-4 py-3" data-testid={`faq-${i}`}>
                    <summary className="cursor-pointer list-none text-[15px] font-semibold text-[var(--ink)] marker:hidden">
                      <span className="mr-2 text-[var(--gov-blue)] group-open:hidden">+</span>
                      <span className="mr-2 hidden text-[var(--gov-blue)] group-open:inline">−</span>
                      {f.q}
                    </summary>
                    <p className="mt-2 pl-5 text-sm leading-relaxed text-[var(--ink-soft)]">{f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* CompuBrain parent-company section */}
          <CompuBrainSection text={data.cb_section} />
        </article>

        {/* RIGHT: related sidebar */}
        <aside className="min-w-0 space-y-6" data-testid="related-sidebar">
          <SidebarList title="Related Categories" items={data.rel_categories} icon={Tag} testid="side-categories" />
          <SidebarList
            title={type === "category" ? "Related Services" : "Related Services"}
            items={data.rel_services}
            icon={Layers}
            testid="side-services"
          />
          <SidebarList
            title={type === "company" ? "Related Platforms" : "Related Platforms"}
            items={data.rel_companies}
            icon={Building2}
            testid="side-companies"
          />
        </aside>
      </div>

      {/* See also (full-width related grids) */}
      <div className="mx-auto max-w-[1240px] px-4 pb-4 lg:px-6">
        <div className="rounded-[6px] border border-[var(--line)] bg-[var(--bg-alt)] p-5 sm:p-6" data-testid="see-also">
          <h2 className="mb-1 text-xl font-bold text-[var(--navy)]">See also</h2>
          <p className="mb-4 text-sm text-[var(--muted)]">
            Continue exploring the knowledge base through related entries.
          </p>
          <div className="grid gap-6 md:grid-cols-3">
            <MiniList title="Services" items={data.rel_services} />
            <MiniList title="Categories" items={data.rel_categories} />
            <MiniList title="Platforms" items={data.rel_companies} />
          </div>
        </div>
      </div>
    </Layout>
  );
}

function SidebarList({ title, items, icon: Icon, testid }) {
  if (!items || items.length === 0) return null;
  const shown = items.slice(0, 14);
  return (
    <div className="rounded-[5px] border border-[var(--line)] bg-white" data-testid={testid}>
      <div className="flex items-center gap-1.5 border-b border-[var(--line-soft)] bg-[var(--bg-alt)] px-3 py-2 text-xs font-bold uppercase tracking-wide text-[var(--navy)]">
        <Icon size={13} /> {title}
      </div>
      <ul className="p-2">
        {shown.map((it) => (
          <li key={`${it.type}-${it.slug}`}>
            <Link
              to={`/${it.type}/${it.slug}`}
              className="block rounded px-2 py-1.5 text-sm text-[var(--link)] hover:bg-[var(--bg-alt)]"
              data-testid={`sidebar-link-${it.type}-${it.slug}`}
            >
              {it.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MiniList({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-[var(--muted)]">{title}</h3>
      <ul className="space-y-1">
        {items.slice(0, 8).map((it) => (
          <li key={`${it.type}-${it.slug}`}>
            <Link to={`/${it.type}/${it.slug}`} className="wikilink text-sm">
              {it.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
